import { RestfulStoredValueWebService, StoredValueWebService } from 'ws/StoredValueWebService';
import { StoredValueAction, StoredValueAgencyRecordsWithPagination, StoredValueLog, StoredValueSummary, StoredValueTotal } from './StoredValueLog';
import { defaultTo } from 'lodash';

export const FREEZE_ACTIONS = [
  StoredValueAction.FREEZE_BUDGET,
  StoredValueAction.UNFREEZE_BUDGET,
  StoredValueAction.REMOVE_FREEZE_BUDGET
];

export interface StoredValueManager {
  getStoredValueAgencyRecords (page: number, sortField: string, sortOrder: string, search: string): Promise<StoredValueAgencyRecordsWithPagination>;
  getStoredValueLogsByAgencyId (agencyId: number): Promise<StoredValueLog[]>;
  getStoredValueSummary (storedValueLogs: StoredValueLog[]): StoredValueSummary[];
  getStoredValueTotal (agencyId: number): Promise<StoredValueTotal>;
  getStoredValueFrozenTotal (agencyId: number): Promise<StoredValueTotal>;
  editStoredValue (agencyId: number, amount: number, action: string, description: string, comment?: string): Promise<void>;
  getRemainingStoredValue (agencyId: number): Promise<number | undefined>;
}

export class DefaultStoredValueManager implements StoredValueManager {
  webService: StoredValueWebService;

  constructor (webService: StoredValueWebService = new RestfulStoredValueWebService()) {
    this.webService = webService;
  }

  async getStoredValueAgencyRecords (page: number, sortField: string, sortOrder: string, search: string): Promise<StoredValueAgencyRecordsWithPagination> {
    return this.webService.getStoredValueAgencyRecords(page, sortField, sortOrder, search);
  }

  async getStoredValueLogsByAgencyId (agencyId: number): Promise<StoredValueLog[]> {
    const logs = await this.webService.getStoredValueLogsByAgencyId(agencyId);
    this.addRemianValueToLogs(logs);
    return logs;
  }

  /**
   * 把一批明細列加總成每家供應商的 summary。
   *
   * 這裡算的是**營運口徑**：RECONCILE_ADJUST（弭平扣除）是純帳務註記，一律跳過，
   * 與後端 StoredValueService#calculateSummary 的營運口徑一致。
   *
   * ⚠️ **remain / freezed 仍然建議用後端的數字**（getStoredValueTotal / getStoredValueFrozenTotal）：
   * 傳進來的 logs 可能被分頁 / 日期條件切過，只是這一頁的加總，不是全期餘額。
   *
   * 目前唯一的 production 呼叫端是 StoredValueDetailModel 的「已用額度 / 總儲值」兩格。
   * 詳見 StoredValueDetailModel#initInfo。
   */
  getStoredValueSummary (storedValueLogs: StoredValueLog[]): StoredValueSummary[] {
    const results = storedValueLogs.reduce<{[id: string]: StoredValueSummary}>((acc, log) => {
      const { adAgencyId, adAgencyName, vendorNumber, currency, amount, action } = log;
      let summaryOfAgency = defaultTo(acc[adAgencyId], {
        adAgencyId,
        adAgencyName,
        vendorNumber,
        currency,
        stored: 0,
        used: 0,
        remain: 0,
        freezed: 0,
        logs: []
      });
      switch (action) {
        case StoredValueAction.RECHARGE:
        case StoredValueAction.EDIT_BUDGET:
          summaryOfAgency.stored += amount;
          summaryOfAgency.remain += amount;
          break;
        case StoredValueAction.CREATE_ORDER:
        case StoredValueAction.EDIT_ORDER:
        case StoredValueAction.SETTLE_ORDER:
          summaryOfAgency.remain += amount;
          summaryOfAgency.used -= amount;
          break;
        case StoredValueAction.RECONCILE_ADJUST:
          // RECONCILE_ADJUST（弭平扣除）是**純帳務註記**，不是真的扣款：
          // stored / used / remain / freezed 一個都不動，等同這一列不存在。
          // 只有後端的儲值額度對帳報表（d上期 / d本期）才會把它算進餘額；
          // 前端顯示的所有數字都是營運口徑，與後端 StoredValueService#calculateSummary 一致。
          break;
        case StoredValueAction.FREEZE_BUDGET:
          summaryOfAgency.remain -= amount;
          summaryOfAgency.freezed += amount;
          break;
        case StoredValueAction.UNFREEZE_BUDGET:
          summaryOfAgency.remain += amount;
          summaryOfAgency.freezed -= amount;
          break;
        case StoredValueAction.REMOVE_FREEZE_BUDGET:
          summaryOfAgency.freezed -= amount;
          break;
        default:
          break;
      }
      acc[adAgencyId] = summaryOfAgency;
      acc[adAgencyId].logs.push(log);
      return acc;
    }, {});
    return Object.values(results);
  }

  async getStoredValueTotal (agencyId: number): Promise<StoredValueTotal> {
    return this.webService.getStoredValueTotal(agencyId);
  }

  async getStoredValueFrozenTotal (agencyId: number): Promise<StoredValueTotal> {
    return this.webService.getStoredValueFrozenTotal(agencyId);
  }

  async editStoredValue (agencyId: number, amount: number, action: string, description: string, comment?: string): Promise<void> {
    return this.webService.editStoredValue(agencyId, amount, action, description, comment);
  }

  /**
   * 可用額度一律問後端，不從 `/no-page` 的明細清單自己加總 ——
   * 建單守門用的是後端的營運餘額，前端顯示要與它逐字相同才不會出現「畫面說夠、後端擋下來」。
   *
   * 後端的 remaining-amount 也是營運口徑（排除純帳務註記 RECONCILE_ADJUST，弭平不會擋單）。
   */
  async getRemainingStoredValue (agencyId: number) {
    const storedValueTotal = await this.getStoredValueTotal(agencyId);
    return storedValueTotal.remainingAmount;
  }

  /**
   * 逐列的「剩餘額度」欄：從最舊那一列往下累加。
   *
   * 營運口徑：RECONCILE_ADJUST（弭平扣除）是純帳務註記，逐列餘額完全不受它影響
   * （與後端 StoredValueService#listStoredValueDTO 的逐列回推一致）。
   * 供應商看不到那一列、管理員看得到但餘額欄不變動，兩邊的 running balance 因此都對得起來。
   */
  private addRemianValueToLogs (logs: StoredValueLog[]) {
    const sortedLogs = logs.sort((a, b) => a.storedValueId - b.storedValueId);
    let remainValueMap = {};
    sortedLogs.forEach(log => {
      const remainValue = defaultTo(remainValueMap[log.adAgencyId], 0);
      switch (log.action) {
        case StoredValueAction.FREEZE_BUDGET:
          log.remain = remainValue - log.amount;
          break;
        case StoredValueAction.RECONCILE_ADJUST:
          // RECONCILE_ADJUST（弭平扣除）是**純帳務註記**，不是真的扣款：逐列餘額直接沿用上一列、
          // 完全不受影響（與 REMOVE_FREEZE_BUDGET 同樣是不動 remain 的分支）。
          // 只有後端的對帳報表會把它算進餘額；畫面上的營運餘額一律不含它。
          // 必須明列，否則會落到 default 的 remain += amount。
          log.remain = remainValue;
          break;
        case StoredValueAction.REMOVE_FREEZE_BUDGET:
          log.remain = remainValue;
          break;
        case StoredValueAction.UNFREEZE_BUDGET:
        default:
          log.remain = remainValue + log.amount;
          break;
      }
      remainValueMap[log.adAgencyId] = log.remain;
    });
  }
}
